/**
 * EXPLOITATION › ASSOCIATIONS — LE MODÈLE DES ÉCRANS (20/10/2026).
 *
 * Les types tels que l'API `/api/v1/associations` les rend, et les règles
 * PURES des écrans (filtres écrits dans l'adresse, comparaison, formats). Ce
 * module n'importe ni React ni feuille de style : il se teste hors
 * navigateur.
 *
 * ═══ LE MOT « ASSOCIATION » ═══
 * Ailleurs dans Gestion, une « association » a longtemps voulu dire un
 * ÉVÉNEMENT Pretix (Réglages › Billetteries, « Venue des associations »).
 * Ici, une association est un ORGANISATEUR (BDE Médecine, ESN…) ; ses
 * événements s'appellent « ses soirées ». Les deux ne se mélangent jamais
 * dans un même libellé.
 *
 * Mots VERROUILLÉS : des billets vendus, des billets scannés (« entrées ») —
 * jamais des « personnes présentes ». Un chiffre inconnu s'écrit « — » ou
 * « inconnu », jamais 0.
 */

export type KindAssociation = 'BDE' | 'ASSO' | 'GROUPEMENT'
export type StatutAssociation = 'SUGGESTED' | 'ACTIVE' | 'ARCHIVED' | 'REJECTED'
export type ModeReconnaissance = 'CERTAIN_AND_PROBABLE' | 'CERTAIN_ONLY' | 'NEVER'
export type VerdictAssociation = 'CERTAIN' | 'PROBABLE' | 'AMBIGU' | 'A_IDENTIFIER' | 'AUCUNE'

export interface Association {
  id: string
  name: string
  kind: KindAssociation
  school: string | null
  member_of_id: string | null
  member_of_name: string | null
  status: StatutAssociation
  note: string | null
  contact_role: string | null
  origin: 'SUGGESTION' | 'MANUEL' | 'SCISSION'
  suggestion_reason: string | null
  merged_into_id: string | null
  merged_into_name: string | null
  created_at: string
  updated_at: string
}

export interface Alias { id: string; association_id?: string; label: string; pattern: string; origin: 'SEED' | 'MANUEL'; created_at?: string }

export interface Mix { essentiel: number; soft: number; prestige: number; autre: number }

export interface Tendance {
  sens: 'HAUSSE' | 'BAISSE' | 'STABLE'
  ecart_pct: number
  serie: Array<{ nuit: string; vendus: number; entrees: number | null }>
}

export interface Indicateurs {
  nuits: number
  derniere_nuit: string | null
  prochaine_nuit: string | null
  vendus_total: number
  /** `null` : aucune soirée dont les entrées sont connues — « — », jamais 0. */
  entrees_total: number | null
  nuits_sans_entrees: number
  /** Soirées dont les entrées ont été saisies (à la main ou par la Comptabilité), pas scannées. */
  nuits_entrees_saisies: number
  moyenne_vendus: number | null
  no_show_pct: number | null
  tendance: Tendance | null
  mix: Mix
  sources: string[]
}

export interface LigneAssociation extends Association {
  alias: Alias[]
  membres: number
  indicateurs: Indicateurs
}

export interface Droits { gerer: boolean; argent: boolean }

export interface ReponseListe {
  associations: LigneAssociation[]
  /** Les chiffres « en un regard » des actives : chaque nuit et chaque billet UNE fois (un groupement ne recompte pas ses membres). */
  totaux: Indicateurs
  /** `a_rattacher` : `null` pour qui ne lit pas les soirées (il n'est alors pas compté). */
  a_decider: { total: number; suggestions: number; evenements: number; nuits_sans_nom: number; a_rattacher: number | null }
  droits: Droits
}

export interface Signaux { alias: string[]; source: string; empreinte: boolean; generiques: string[] }

export interface EvenementADecider {
  event_id: string
  name: string
  source: string
  nuit: string
  vendus: number
  verdict: VerdictAssociation
  raison: string
  suggestion: { id: string; name: string } | null
  candidats: Array<{ id: string; name: string }>
  signaux: Signaux
}

export interface SuggestionADecider {
  association: Association
  alias: Alias[]
  reconnues: Array<{ event_id: string; name: string; source: string; nuit: string; vendus: number; confidence: 'CERTAIN' | 'PROBABLE' }>
}

/**
 * Une nuit sans nom, candidate : une soirée Shotgun vendue en bloc (BILLETS),
 * ou une nuit que seule la Comptabilité connaît (COMPTABILITE, sans soirée).
 */
export interface NuitSansNom {
  cle: string
  origine: 'BILLETS' | 'COMPTABILITE'
  event_id: string | null
  name: string | null
  nuit: string
  vendus: number | null
  produits: string[]
  entrees_compta: number | null
  raison: string
}

export interface ReponseADecider {
  suggestions: SuggestionADecider[]
  evenements: EvenementADecider[]
  nuits_sans_nom: NuitSansNom[]
  a_rattacher: number | null
  total: number
  motif_min: number
  droits: Droits
}

/** Ce qui a été écarté, pour y revenir après le message « ANNULER ». */
export interface Ecartees {
  suggestions: Array<{ id: string; name: string; kind: KindAssociation; at: string; reason: string | null; geste_id: string | null }>
  evenements: Array<{ event_id: string; name: string; source: string; nuit: string; vendus: number; at: string; reason: string | null
    actor_name: string | null; geste_id: string | null; geste_evenements: number }>
  nuits: Array<{ nuit: string; at: string; reason: string | null; actor_name: string | null; geste_id: string | null }>
  droits: Droits
}

export interface Resume { actives: number; suggestions: number; a_decider: number; prochaine: { association_id: string; name: string; nuit: string } | null }

export interface EvenementDeNuit {
  id: string
  name: string
  source: string
  status: 'SCHEDULED' | 'CANCELLED'
  starts_at: string
  vendus: number
  entres: number
  formules: Record<'ESSENTIEL' | 'SOFT' | 'PRESTIGE' | 'AUTRE', number>
  premiere_vente: string | null
  mi_vente: string | null
  vendus_apres_ouverture: number
  lien: { method: 'AUTO' | 'MANUEL'; confidence: 'CERTAIN' | 'PROBABLE' | 'MANUEL'; group_id: string | null; group_name: string | null }
}

export interface NuitManuelle {
  id: string
  entries_hint: number | null
  entries_compta: number | null
  nuit_partagee: boolean
  note: string | null
  decided_at: string
}

export interface IndicateursNuit {
  nuit: string
  vendus: number | null
  entrees: number | null
  entrees_source: 'SCANS' | 'MANUEL' | 'COMPTA' | null
  no_show_pct: number | null
  mix: Mix | null
  ventes_avant_jours: number | null
  mi_ventes_avant_jours: number | null
  porte: boolean
  terminee: boolean
  sources: string[]
  manuelle: boolean
  groupes: string[]
}

export interface NuitDeLaFiche {
  nuit: { nuit: string; evenements: EvenementDeNuit[]; manuelle: NuitManuelle | null }
  indicateurs: IndicateursNuit
  /** Comptée parmi « Soirées » (une page vide passée est montrée, pas comptée). */
  comptee: boolean
}

export interface Operations {
  nuit: string
  avant_suivi: boolean
  agents_pic: number | null
  staff_pic: number | null
  incidents: number | null
  incidents_critiques: number | null
}

export interface ArgentNuit {
  nuit: string
  face_cents: number | null
  share_cents: number | null
  kept_cents: number | null
  fees_cents: number | null
  inconnu: boolean
  raison: string | null
  part_par_billet_cents: number | null
  part_origine: 'EVENEMENT' | 'DEFAUT' | 'MIXTE' | null
}

export interface Argent {
  nuits: ArgentNuit[]
  face_cents: number | null
  share_cents: number | null
  kept_cents: number | null
  fees_cents: number | null
  nuits_connues: number
  face_part_inconnue_cents: number | null
  nuits_inconnues: number
  notes: string[]
}

export interface GesteHistorique {
  id: string
  kind: string
  summary: string
  reason: string | null
  at: string
  actor_name: string | null
  undone: boolean
  undoes_id: string | null
}

export interface ProchaineSoiree {
  nuit: string
  vendus: number
  evenements: Array<{ event_id: string; name: string; etat: 'RATTACHEE' | 'SEPAREE' | 'A_RATTACHER' | 'CLUB'; parent_name: string | null; method: 'AUTO' | 'MANUEL' | null }>
}

export interface Fiche {
  association: Association
  alias: Alias[]
  membres: Array<{ id: string; name: string; status: StatutAssociation }>
  nuits: NuitDeLaFiche[]
  indicateurs: Indicateurs
  prochaine: ProchaineSoiree | null
  operations: Operations[]
  argent: Argent | null
  argent_masque: boolean
  historique: GesteHistorique[]
  droits: Droits
  motif_min: number
  /** L'alerte « à rattacher avant la nuit » de sa prochaine soirée (20/10/2026) ; `null` : aucun cas. */
  alerte_prochaine?: { cle: string; genre: 'A_RATTACHER' | 'SEULE'; etat: string; phrase: string } | null
  /** Le rattachement automatique réglé (ce que Gestion fera seul quand le club ouvrira sa soirée) ; absent sans « Consulter les soirées ». */
  attach_mode?: 'CERTAIN_AND_PROBABLE' | 'CERTAIN_ONLY' | 'NEVER'
}

export interface Comparaison {
  association: { id: string; name: string; kind: KindAssociation; status: StatutAssociation }
  indicateurs: Indicateurs
  serie: Array<{ nuit: string; vendus: number | null; entrees: number | null; no_show_pct: number | null; mix: Mix | null; ventes_avant_jours: number | null }>
}

export interface ReglagesAssociations {
  recognition_mode: ModeReconnaissance
  excluded_words: string[]
  night_product_words: string[]
  generic_words: string[]
  trend_nights: number
  show_on_soiree: boolean
  show_on_calendar: boolean
  show_on_home: boolean
  notify_auto_links: boolean
  count_manual_nights: boolean
}

export interface BornesReglages { trend_min: number; trend_max: number; mots_max: number; mot_longueur_max: number; formules_min?: number }

/** Deux formules au moins font une soirée « certaine » par ses produits (le serveur dit la même chose). */
export const FORMULES_MIN = 2

/** Le résultat d'un geste : son identifiant (pour « ANNULER ») et la phrase à dire. */
export type ResultatGeste =
  | { ok: true; geste: string; message: string; association_id: string | null; inchanges: number }
  | { ok?: false; error?: string; message?: string }

/* ═══════════════════════════════════════ constantes ═══ */

export const CHEMIN_LISTE = '/exploitation/associations'
export const CHEMIN_COMPARER = '/exploitation/associations/comparer'
export const CHEMIN_REGLAGES = '/settings/soirees/associations'
/** Le bloc « À décider » de la liste : la cible des notifications. */
export const VUE_A_DECIDER = 'a-decider'
export const COMPARER_MIN = 2
export const COMPARER_MAX = 6

/** Dit à tous les écrans ouverts (liste, fiche, badge, tuile) de se relire après un geste. */
export const EVT_ASSOCIATIONS = 'gestion:associations'

export function signalerAssociations(): void {
  try { window.dispatchEvent(new Event(EVT_ASSOCIATIONS)) } catch { /* hors navigateur */ }
}

export const cheminFiche = (id: string) => `${CHEMIN_LISTE}/${id}`
export const cheminComparer = (ids: readonly string[]) => `${CHEMIN_COMPARER}?ids=${ids.join(',')}`

export const LIBELLES_KIND: Record<KindAssociation, string> = { BDE: 'BDE', ASSO: 'Association', GROUPEMENT: 'Groupement' }
export const LIBELLES_STATUT: Record<StatutAssociation, string> = {
  SUGGESTED: 'Suggérée', ACTIVE: 'Active', ARCHIVED: 'Archivée', REJECTED: 'Rejetée',
}
export const LIBELLES_SOURCE: Record<string, string> = { shotgun: 'Shotgun', pretix: 'Pretix', manuel: 'à la main' }
export const LIBELLES_MODE: Record<ModeReconnaissance, string> = {
  CERTAIN_AND_PROBABLE: 'Quand c’est certain ou probable',
  CERTAIN_ONLY: 'Seulement quand c’est certain',
  NEVER: 'Jamais : tout passe par « À décider »',
}

export const libelleSource = (s: string) => LIBELLES_SOURCE[s] ?? s

/* ═══════════════════════════════════ filtres dans l'adresse ═══ */

export type FiltreStatut = 'actives' | 'archivees' | 'toutes'
export type TriListe = 'nom' | 'prochaine' | 'vendus' | 'derniere'

export interface FiltresListe {
  statut: FiltreStatut
  q: string
  /** Type d'association ; vide = tous. */
  kind: KindAssociation | ''
  tri: TriListe
  /** Saison « 2025-2026 » (septembre → août), vide = toutes. Filtre sur la dernière soirée. */
  saison: string
}

export const FILTRES_DEFAUT: FiltresListe = { statut: 'actives', q: '', kind: '', tri: 'prochaine', saison: '' }

const STATUTS: readonly FiltreStatut[] = ['actives', 'archivees', 'toutes']
const TRIS: readonly TriListe[] = ['nom', 'prochaine', 'vendus', 'derniere']
const KINDS: readonly KindAssociation[] = ['BDE', 'ASSO', 'GROUPEMENT']

/** Lit les filtres d'une adresse : toute valeur inconnue retombe sur le défaut (un lien bricolé ne casse rien). */
export function filtresDepuisUrl(p: URLSearchParams): FiltresListe {
  const statut = p.get('statut') as FiltreStatut | null
  const tri = p.get('tri') as TriListe | null
  const kind = p.get('type') as KindAssociation | null
  const saison = p.get('saison') ?? ''
  return {
    statut: statut && STATUTS.includes(statut) ? statut : FILTRES_DEFAUT.statut,
    q: (p.get('q') ?? '').slice(0, 80),
    kind: kind && KINDS.includes(kind) ? kind : '',
    tri: tri && TRIS.includes(tri) ? tri : FILTRES_DEFAUT.tri,
    saison: /^\d{4}-\d{4}$/.test(saison) && Number(saison.slice(5)) === Number(saison.slice(0, 4)) + 1 ? saison : '',
  }
}

/**
 * Écrit les filtres dans l'adresse — SEULEMENT ceux qui diffèrent du défaut :
 * la liste sans filtre garde une adresse nue, et la vue « À décider »
 * (`?vue=a-decider`, cible des notifications) est conservée telle quelle.
 */
export function filtresVersUrl(f: FiltresListe, vue: string | null = null): URLSearchParams {
  const p = new URLSearchParams()
  if (vue) p.set('vue', vue)
  if (f.statut !== FILTRES_DEFAUT.statut) p.set('statut', f.statut)
  if (f.q.trim()) p.set('q', f.q.trim())
  if (f.kind) p.set('type', f.kind)
  if (f.tri !== FILTRES_DEFAUT.tri) p.set('tri', f.tri)
  if (f.saison) p.set('saison', f.saison)
  return p
}

/** La saison d'une nuit : de septembre à août (« 2025-2026 »). */
export function saisonDe(nuit: string): string {
  const y = Number(nuit.slice(0, 4))
  const m = Number(nuit.slice(5, 7))
  return m >= 9 ? `${y}-${y + 1}` : `${y - 1}-${y}`
}

/** Les saisons présentes dans la liste (dernière et prochaine soirée), la plus récente d'abord. */
export function saisonsDe(lignes: readonly LigneAssociation[]): string[] {
  const s = new Set<string>()
  for (const l of lignes) {
    if (l.indicateurs.derniere_nuit) s.add(saisonDe(l.indicateurs.derniere_nuit))
    if (l.indicateurs.prochaine_nuit) s.add(saisonDe(l.indicateurs.prochaine_nuit))
  }
  return [...s].sort().reverse()
}

/** Comparaison sans accents ni casse (« medecine » trouve « Médecine »). */
export function plier(s: string): string {
  return s.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase().trim()
}

/**
 * La liste filtrée et triée, côté écran. La page lit TOUTES les associations
 * (sauf les rejetées) une seule fois : les choix « lier à… » ont besoin des
 * actives même quand la liste montre les archivées.
 */
export function appliquerFiltres(lignes: readonly LigneAssociation[], f: FiltresListe): LigneAssociation[] {
  const q = plier(f.q)
  const gardees = lignes.filter((l) => {
    if (l.status === 'REJECTED' || l.status === 'SUGGESTED') return false
    if (f.statut === 'actives' && l.status !== 'ACTIVE') return false
    if (f.statut === 'archivees' && l.status !== 'ARCHIVED') return false
    if (f.kind && l.kind !== f.kind) return false
    if (f.saison) {
      const d = l.indicateurs.derniere_nuit
      const n = l.indicateurs.prochaine_nuit
      if (!(d && saisonDe(d) === f.saison) && !(n && saisonDe(n) === f.saison)) return false
    }
    if (!q) return true
    return [l.name, l.school ?? '', l.member_of_name ?? '', ...l.alias.map((a) => a.label)].some((x) => plier(x).includes(q))
  })
  const nom = (a: LigneAssociation, b: LigneAssociation) => a.name.localeCompare(b.name, 'fr')
  const tri: Record<TriListe, (a: LigneAssociation, b: LigneAssociation) => number> = {
    nom,
    vendus: (a, b) => b.indicateurs.vendus_total - a.indicateurs.vendus_total || nom(a, b),
    derniere: (a, b) => (b.indicateurs.derniere_nuit ?? '').localeCompare(a.indicateurs.derniere_nuit ?? '') || nom(a, b),
    // La prochaine soirée d'abord (la plus proche), puis les plus récentes.
    prochaine: (a, b) => {
      const pa = a.indicateurs.prochaine_nuit
      const pb = b.indicateurs.prochaine_nuit
      if (pa && pb) return pa.localeCompare(pb) || nom(a, b)
      if (pa) return -1
      if (pb) return 1
      return (b.indicateurs.derniere_nuit ?? '').localeCompare(a.indicateurs.derniere_nuit ?? '') || nom(a, b)
    },
  }
  return [...gardees].sort(tri[f.tri])
}

export function filtresActifs(f: FiltresListe): boolean {
  return f.statut !== FILTRES_DEFAUT.statut || f.q.trim() !== '' || f.kind !== '' || f.saison !== ''
}

/* ═══════════════════════════════════════ comparer ═══ */

const UUID = /^[0-9a-f-]{36}$/

/** Les associations à comparer, lues dans `?ids=` : identifiants valides, sans doublon, au plus 6. */
export function idsAComparer(p: URLSearchParams): string[] {
  const brut = (p.get('ids') ?? '').split(',').map((x) => x.trim()).filter((x) => UUID.test(x))
  return [...new Set(brut)].slice(0, COMPARER_MAX)
}

/** Ajoute ou retire une association de la sélection « Comparer », jamais au-delà de 6. */
export function basculerSelection(sel: readonly string[], id: string): string[] {
  if (sel.includes(id)) return sel.filter((x) => x !== id)
  if (sel.length >= COMPARER_MAX) return [...sel]
  return [...sel, id]
}

/** La part de chaque formule dans un mélange, en % entiers ; `null` sans billet. */
export function partsMix(m: Mix | null): Mix | null {
  if (!m) return null
  const t = m.essentiel + m.soft + m.prestige + m.autre
  if (t === 0) return null
  const pct = (x: number) => Math.round((x / t) * 100)
  return { essentiel: pct(m.essentiel), soft: pct(m.soft), prestige: pct(m.prestige), autre: pct(m.autre) }
}

/* ═══════════════════════════════════════ formats ═══ */

const fmtEntier = new Intl.NumberFormat('fr-FR')
const fmtEur = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0 })
const fmtEurCents = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 })

/** Un nombre inconnu s'écrit « — », jamais 0. */
export function nombre(n: number | null | undefined): string {
  return n === null || n === undefined ? '—' : fmtEntier.format(n)
}

export function pourcent(n: number | null | undefined): string {
  return n === null || n === undefined ? '—' : `${String(n).replace('.', ',')} %`
}

/** Un montant en centimes ; inconnu → « inconnu » (jamais 0 €). */
export function euros(cents: number | null | undefined, precis = false): string {
  if (cents === null || cents === undefined) return 'inconnu'
  return (precis ? fmtEurCents : fmtEur).format(cents / 100)
}

/** « jeu. 15/10/2026 » — la nuit, jour de la semaine compris. */
export function nuitLongue(nuit: string): string {
  const d = new Date(`${nuit}T12:00:00Z`)
  if (Number.isNaN(d.getTime())) return nuit
  const jour = d.toLocaleDateString('fr-FR', { weekday: 'short', timeZone: 'UTC' })
  return `${jour} ${nuit.slice(8, 10)}/${nuit.slice(5, 7)}/${nuit.slice(0, 4)}`
}

/** « 15/10/26 » — pour les axes et les cellules étroites. */
export function nuitCourte(nuit: string): string {
  return `${nuit.slice(8, 10)}/${nuit.slice(5, 7)}/${nuit.slice(2, 4)}`
}

export function joursAvant(j: number | null): string {
  if (j === null) return '—'
  if (j < 1) return 'le jour même'
  const r = Math.round(j)
  return `${r} jour${r > 1 ? 's' : ''} avant`
}

export function texteTendance(t: Tendance | null): string {
  if (!t) return 'Pas assez de soirées'
  const signe = t.ecart_pct > 0 ? '+' : ''
  const sens = t.sens === 'HAUSSE' ? 'En hausse' : t.sens === 'BAISSE' ? 'En baisse' : 'Stable'
  return `${sens} (${signe}${t.ecart_pct} %)`
}

/** Ce que veut dire le lien d'une soirée, en clair. */
export function texteLien(l: EvenementDeNuit['lien']): string {
  if (l.method === 'MANUEL') return 'liée à la main'
  return l.confidence === 'CERTAIN' ? 'reconnue automatiquement (certain)' : 'reconnue automatiquement (probable)'
}

export function texteVerdict(v: VerdictAssociation): string {
  switch (v) {
    case 'CERTAIN': return 'Reconnue (certain)'
    case 'PROBABLE': return 'Reconnue au nom (probable)'
    case 'AMBIGU': return 'Plusieurs associations possibles'
    case 'A_IDENTIFIER': return 'Association à identifier'
    default: return 'Non reconnue'
  }
}

export function compte(n: number, un: string, plusieurs: string): string {
  return `${fmtEntier.format(n)} ${n > 1 ? plusieurs : un}`
}

/** La phrase du badge et de la tuile : ce qui attend une décision. */
export function texteADecider(n: number): string {
  return n === 0 ? 'Rien à décider' : `${fmtEntier.format(n)} à décider`
}

/**
 * Le détail du compte « À décider », dans l'ordre de l'urgence : ce qui doit
 * être rattaché avant la nuit d'abord. « À rattacher » vit dans le même bloc :
 * il compte, sinon la tuile disait « rien à décider » au-dessus d'une carte
 * urgente.
 */
export function detailADecider(d: { suggestions: number; evenements: number; nuits_sans_nom: number; a_rattacher: number | null }): string {
  const parts = [
    d.a_rattacher ? compte(d.a_rattacher, 'à rattacher', 'à rattacher') : null,
    d.suggestions ? compte(d.suggestions, 'suggestion', 'suggestions') : null,
    d.evenements ? compte(d.evenements, 'soirée à identifier', 'soirées à identifier') : null,
    d.nuits_sans_nom ? compte(d.nuits_sans_nom, 'nuit sans nom', 'nuits sans nom') : null,
  ].filter((x): x is string => !!x)
  return parts.length === 0 ? 'Rien à décider' : parts.join(' · ')
}

/**
 * L'aide sous le total des entrées. Le libellé dit « Entrées », pas
 * « scannées » seul : sur une nuit marquée à la main, les entrées sont
 * saisies (par l'exploitant, ou celles de la Comptabilité) — c'est dit ici.
 */
export function aideEntrees(i: Pick<Indicateurs, 'nuits_sans_entrees' | 'nuits_entrees_saisies'>): string {
  const parts = ['billets scannés, pas des personnes présentes']
  if (i.nuits_entrees_saisies > 0) parts.push(`dont ${compte(i.nuits_entrees_saisies, 'nuit marquée à la main, aux entrées saisies', 'nuits marquées à la main, aux entrées saisies')}`)
  if (i.nuits_sans_entrees > 0) parts.push(`${compte(i.nuits_sans_entrees, 'soirée sans entrées connues, hors du total', 'soirées sans entrées connues, hors du total')}`)
  return parts.join(' ; ')
}

/**
 * L'aide du champ « Entrées » d'une nuit marquée — PURE, selon l'origine.
 * Une nuit sans nom d'origine BILLETS a, par définition, une soirée du club
 * avec au moins 100 billets : marquée « seulement la nuit », elle est
 * PARTAGÉE et la Comptabilité (toute la nuit) ne lui est jamais créditée.
 * Promettre « celles de la Comptabilité » laissait la nuit à « — ».
 */
export function aideEntreesNuit(n: Pick<NuitSansNom, 'origine' | 'entrees_compta'>): string {
  if (n.origine === 'COMPTABILITE' && n.entrees_compta !== null) {
    return `Laissé vide : les ${nombre(n.entrees_compta)} entrées de la Comptabilité, la seule trace de cette nuit.`
  }
  return 'Saisissez-les si vous les connaissez : la Comptabilité compte toute la nuit, billets du club compris, et ne peut pas les lui attribuer. Laissé vide : « — ».'
}

/* ═══════════════════════════════════════ réglages ═══ */

export const REGLAGES_DEFAUT: ReglagesAssociations = {
  recognition_mode: 'CERTAIN_AND_PROBABLE',
  excluded_words: ['étudiant', 'campus', 'soirée'],
  night_product_words: ['Essentiel', 'Soft Mood', 'Prestige'],
  generic_words: ['BDE', 'BDS', 'BDA', 'soirée privée', 'post partiel'],
  trend_nights: 5,
  show_on_soiree: true,
  show_on_calendar: true,
  show_on_home: true,
  notify_auto_links: true,
  count_manual_nights: true,
}

export function reglagesMemes(a: ReglagesAssociations, b: ReglagesAssociations): boolean {
  return (Object.keys(REGLAGES_DEFAUT) as Array<keyof ReglagesAssociations>).every((k) => JSON.stringify(a[k]) === JSON.stringify(b[k]))
}

/** Les champs qui diffèrent : le compte de la Save Bar. */
export function reglagesChanges(a: ReglagesAssociations, b: ReglagesAssociations): number {
  return (Object.keys(REGLAGES_DEFAUT) as Array<keyof ReglagesAssociations>).filter((k) => JSON.stringify(a[k]) !== JSON.stringify(b[k])).length
}

/** La raison qui bloque l'enregistrement, ou `null`. */
export function reglagesInvalides(r: ReglagesAssociations, b: BornesReglages): string | null {
  if (!Number.isInteger(r.trend_nights) || r.trend_nights < b.trend_min || r.trend_nights > b.trend_max) {
    return `Tendance : entre ${b.trend_min} et ${b.trend_max} soirées.`
  }
  for (const [k, l] of [['Mots exclus', r.excluded_words], ['Formules', r.night_product_words], ['Mots qui signalent une association', r.generic_words]] as const) {
    if (l.length > b.mots_max) return `${k} : ${b.mots_max} mots au plus.`
    if (l.some((x) => x.length > b.mot_longueur_max)) return `${k} : ${b.mot_longueur_max} caractères au plus par mot.`
  }
  const min = b.formules_min ?? FORMULES_MIN
  if (r.night_product_words.length < min) {
    return `Formules : gardez au moins ${min} mots. Une soirée Shotgun n’est « certaine » par ses produits que si elle vend deux de ces formules : avec un seul mot, une table « Prestige » du club suffirait.`
  }
  return null
}

/** Ajoute un mot à une liste : nettoyé, sans doublon (accents et casse ignorés). */
export function ajouterMot(liste: readonly string[], mot: string): string[] {
  const t = mot.replace(/\s+/g, ' ').trim()
  if (!t || liste.some((x) => plier(x) === plier(t))) return [...liste]
  return [...liste, t]
}

/* ═══════════════════════════════════════ identité (Save Bar) ═══ */

export interface FormIdentite { name: string; kind: KindAssociation; school: string; member_of_id: string; note: string; contact_role: string }

export const formDe = (a: Association): FormIdentite => ({
  name: a.name, kind: a.kind, school: a.school ?? '', member_of_id: a.member_of_id ?? '', note: a.note ?? '', contact_role: a.contact_role ?? '',
})

/** Le correctif à envoyer : seulement ce qui a changé (l'API refuse un corps vide). */
export function correctifIdentite(avant: FormIdentite, apres: FormIdentite): Record<string, string | null> {
  const out: Record<string, string | null> = {}
  if (apres.name.trim() !== avant.name) out['name'] = apres.name.trim()
  if (apres.kind !== avant.kind) out['kind'] = apres.kind
  const texte = (k: 'school' | 'note' | 'contact_role') => { if (apres[k].trim() !== avant[k]) out[k] = apres[k].trim() || null }
  texte('school'); texte('note'); texte('contact_role')
  const groupe = apres.kind === 'GROUPEMENT' ? '' : apres.member_of_id
  if (groupe !== avant.member_of_id) out['member_of_id'] = groupe || null
  return out
}
